import React, { useState, useEffect, useCallback } from 'react';
import YachtCard from '../YachtCard/YachtCard';
import YachtFleetHeader from '../YachtFleetHeader/Header'; // Changed from ../FleetHeader/Header
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { yachts as yachtApi } from '../../services/api';
import { useLocation } from 'react-router-dom';

const YachtFleet = () => {
  const [yachts, setYachts] = useState([]); // Renamed from cars
  const [filteredYachts, setFilteredYachts] = useState([]); // Renamed from filteredCars
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation(); // Get location state

  // Get initial filter from location state if available
  const initialFilter = location.state?.yachtType || 'All'; // Default to 'All'
  const [activeFilter, setActiveFilter] = useState(initialFilter);

  useEffect(() => {
    const fetchYachts = async () => { // Renamed from fetchCars
      setLoading(true);
      setError(null);
      try {
        const response = await yachtApi.getAll(); // Use yachtApi.getAll
        console.log("Fetched Yachts:", response.data); // Debug log
        if (Array.isArray(response.data)) {
          setYachts(response.data);
          setFilteredYachts(response.data); // Initialize filtered yachts
        } else {
          console.error("API did not return an array:", response.data);
          setError('Received invalid data format for yachts.');
          setYachts([]);
          setFilteredYachts([]);
        }
      } catch (err) {
        console.error("Error fetching yachts:", err); // Debug log
        setError(err.message || 'Failed to fetch yachts.');
        setYachts([]);
        setFilteredYachts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchYachts();
  }, []);

  // Update filteredYachts when activeFilter or yachts change
  useEffect(() => {
    if (activeFilter === 'All') {
      setFilteredYachts(yachts);
    } else {
      // Filter based on yachtType (adjust property name if needed in your Yacht model)
      const filtered = yachts.filter(yacht => yacht.yachtType === activeFilter);
      setFilteredYachts(filtered);
    }
  }, [activeFilter, yachts]);

  console.log("Filtered Yachts:", filteredYachts); // Debug log


  // Callback for handling filter changes from YachtFleetHeader
  const handleFilterChange = useCallback((selectedOptions) => {
    console.log("Filter changed:", selectedOptions);

    // Apply filters based on selected options
    let currentlyFiltered = [...yachts]; // Start with all yachts

    if (selectedOptions && selectedOptions.length > 0) {
      currentlyFiltered = currentlyFiltered.filter(yacht => {
        return selectedOptions.every(option => {
          const { value, label } = option;
          // Determine filter type based on label content
          if (label.includes('Guests')) {
            return (yacht.guestCapacity || yacht.guests) >= value;
          }
          if (label.includes('Cabins')) {
            return yacht.cabins === value;
          }
          if (label.includes('Length')) {
            const yachtLength = parseFloat(yacht.specifications?.length || yacht.length);
            return !isNaN(yachtLength) && yachtLength >= value;
          }
          if (label.includes('Built')) {
            return (yacht.yearBuilt || yacht.year) === value;
          }
          // Add more filter conditions if needed
          return true; // Default pass if filter type not recognized
        });
      });
    }

    setFilteredYachts(currentlyFiltered);
  }, [yachts]); // Depend on the full yacht list


  if (loading) return <LoadingSpinner />;
  if (error) return <div className="text-center text-red-500 py-10">Error: {error}</div>;
  if (!Array.isArray(filteredYachts)) {
    console.error("filteredYachts is not an array:", filteredYachts);
    return <div className="text-center text-red-500 py-10">Error: Invalid yacht data.</div>;
  }


  return (
    <section id="yacht-fleet" className="py-12 bg-gray-50">
      <div className="container mx-auto px-4">
        <YachtFleetHeader
          title="Our Fleet" // Generic title for the main fleet view
          yachts={yachts} // Pass all yachts for filter options
          onFilterChange={handleFilterChange}
          // Removed onPrev/onNext as this isn't a carousel header in this context
        />

        {filteredYachts.length === 0 && !loading ? (
          <div className="text-center text-gray-600 py-10">
            No yachts found matching your criteria.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-8">
            {filteredYachts.map((yacht) => (
              <YachtCard key={yacht._id || yacht.stringId} yacht={yacht} /> // Pass yacht data
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default YachtFleet;
